import {
  ProgressBar as ReactAriaProgressBar,
  Label as ReactAriaLabel,
} from "react-aria-components";

import { customTv, cn } from "#utils";

const containerStyles = customTv({
  base: "w-full overflow-hidden rounded-lg",
  variants: {
    variant: {
      rounded: "",
      square: "rounded-none",
    },
    size: {
      // The default bar: tall, outlined, transparent track.
      medium: "h-4 border border-outline-brand",
      // A thin inline loading bar (e.g. under a list section) with a subtle track.
      slim: "h-[3px] bg-overlay-medium",
    },
  },
  defaultVariants: {
    variant: "rounded",
    size: "medium",
  },
});

const barStyles = customTv({
  base: "h-full rounded-lg bg-foreground-brand transition-all duration-300 ease-out",
  variants: {
    variant: {
      rounded: "",
      square: "rounded-none",
    },
  },
  defaultVariants: {
    variant: "rounded",
  },
});

const labelStyles = customTv({
  base: "text-sm text-gray-700 font-medium",
});

export interface ProgressBarProps {
  /** Visual label shown above the bar. Required when `indeterminate` is false. */
  label?: string;
  /** Required when `indeterminate` is false or omitted */
  percentage?: number;
  /** When true, renders a shimmer animation indicating unknown progress */
  indeterminate?: boolean;
  /** Controls corner rounding. Defaults to "rounded". */
  variant?: "rounded" | "square";
  /** Bar thickness. "slim" is a thin inline loading bar. Defaults to "medium". */
  size?: "medium" | "slim";
}

export function ProgressBar({
  label,
  percentage = 0,
  indeterminate = false,
  variant = "rounded",
  size = "medium",
}: ProgressBarProps) {
  return (
    <ReactAriaProgressBar
      aria-label={!label || indeterminate ? (label ?? "Loading") : undefined}
      className="w-full"
      isIndeterminate={indeterminate}
      value={indeterminate ? undefined : percentage}
    >
      {({ percentage: pct, valueText }) => (
        <>
          {!indeterminate ? (
            <div className={cn("flex items-center justify-between", "mb-2")}>
              <ReactAriaLabel className={cn(labelStyles())}>{label}</ReactAriaLabel>
              <span className="text-sm text-gray-700 ml-2 font-medium">{valueText}</span>
            </div>
          ) : null}
          <div className={cn(containerStyles({ variant, size }))}>
            {indeterminate ? (
              <div className="relative h-full w-full overflow-hidden">
                <div
                  className={cn(
                    "absolute inset-y-0 left-0 w-1/2 animate-loading-overlay-slide",
                    cn("bg-foreground-brand", variant === "rounded" ? "rounded-lg" : undefined),
                  )}
                />
              </div>
            ) : (
              <div className={cn(barStyles({ variant }))} style={{ width: `${pct}%` }} />
            )}
          </div>
        </>
      )}
    </ReactAriaProgressBar>
  );
}
